import { useEffect, useRef, useState } from "react";
import { LOGO } from "./oscarData";

/* Oscar Radar splash intro (premium cut).
   1. navy stage, gold hairline rings draw outward and a radar sweep starts
   2. the new Oscar Radar mark emerges with a light flare and a single gold shimmer; people-dots land on the rings
   3. the wordmark reveals letter by letter, a gold rule draws, tagline + version + parent company settle in
   4. a slim gold progress line, Skip button
   5. a curtain closes from top and bottom with the mark on the seam, the intro is swapped out behind it,
      the curtain parts and the sign-in screen arrives. */

const INTRO_MS = 5400;
const REDUCED = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const WORD = "OSCAR RADAR".split("");

const CSS = `
.rs-root{position:fixed;inset:0;z-index:5000;background:radial-gradient(80% 60% at 50% 38%,#1B3A66 0%,#102542 48%,#08121F 100%);color:#F7F2E8;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;font-family:'Outfit',sans-serif;overflow:hidden;transition:opacity .35s ease}
.rs-root::before{content:"";position:absolute;inset:0;background:radial-gradient(circle at 50% 38%,rgba(198,161,91,.16),transparent 42%);opacity:0;animation:rsGlow 2.4s .5s ease forwards;pointer-events:none}
.rs-root::after{content:"";position:absolute;inset:0;pointer-events:none;box-shadow:inset 0 0 120px 30px rgba(3,8,16,.7)}
@keyframes rsGlow{to{opacity:1}}
.rs-root.rs-gone{opacity:0;pointer-events:none}
.rs-stage{position:relative;width:min(84vw,340px);aspect-ratio:1;flex:none;z-index:1}
.rs-stage canvas{position:absolute;inset:0;width:100%;height:100%}
.rs-logo-wrap{position:absolute;left:50%;top:50%;width:54%;aspect-ratio:1;transform:translate(-50%,-50%);border-radius:50%;opacity:0;animation:rsEmerge 1.3s 1.05s cubic-bezier(.2,.85,.25,1) forwards}
.rs-logo-wrap img{display:block;width:100%;height:100%;border-radius:50%;filter:drop-shadow(0 14px 34px rgba(0,0,0,.6)) drop-shadow(0 0 26px rgba(198,161,91,.35))}
.rs-shine{position:absolute;inset:0;border-radius:50%;overflow:hidden;pointer-events:none}
.rs-shine::before{content:"";position:absolute;top:-20%;bottom:-20%;left:-60%;width:36%;background:linear-gradient(105deg,transparent,rgba(255,244,214,.75),transparent);transform:skewX(-18deg);filter:blur(3px);animation:rsShine 1.5s 2.3s ease-in-out forwards}
@keyframes rsShine{to{left:130%}}
@keyframes rsEmerge{0%{opacity:0;transform:translate(-50%,-50%) scale(.62);filter:blur(16px)}60%{opacity:1;filter:blur(0)}100%{opacity:1;transform:translate(-50%,-50%) scale(1);filter:none}}
.rs-flare{position:absolute;left:50%;top:50%;width:54%;aspect-ratio:1;border-radius:50%;border:2px solid rgba(243,225,172,.9);transform:translate(-50%,-50%) scale(.9);opacity:0;animation:rsFlare 1.6s 1.25s cubic-bezier(.1,.7,.3,1) forwards;box-shadow:0 0 30px rgba(198,161,91,.5)}
.rs-flare.f2{animation-delay:1.55s;border-color:rgba(198,161,91,.55)}
@keyframes rsFlare{0%{opacity:.95;transform:translate(-50%,-50%) scale(.95)}100%{opacity:0;transform:translate(-50%,-50%) scale(2.0)}}
.rs-word{margin:26px 0 0;display:flex;justify-content:center;font-size:clamp(1.35rem,6.4vw,1.85rem);font-weight:700;letter-spacing:.34em;line-height:1;padding-left:.34em;z-index:1}
.rs-word span{display:inline-block;opacity:0;transform:translateY(14px);filter:blur(6px);animation:rsLetter .8s cubic-bezier(.2,.8,.2,1) forwards;background:linear-gradient(180deg,#FFFFFF 0%,#F7F2E8 45%,#D9B96E 100%);-webkit-background-clip:text;background-clip:text;color:transparent}
.rs-word span.sp{width:.5em}
@keyframes rsLetter{to{opacity:1;transform:none;filter:none}}
.rs-rule{margin:16px 0 0;height:1px;width:0;background:linear-gradient(90deg,transparent,#E3C98C,transparent);box-shadow:0 0 12px rgba(198,161,91,.6);animation:rsRule 1.1s 2.7s cubic-bezier(.2,.8,.2,1) forwards;z-index:1}
@keyframes rsRule{to{width:min(62vw,240px)}}
.rs-tag{margin:16px 0 0;font-size:clamp(1rem,4.2vw,1.2rem);font-weight:500;letter-spacing:.04em;text-align:center;color:rgba(247,242,232,.9);z-index:1}
.rs-tag span{display:inline-block;opacity:0;transform:translateY(8px);animation:rsRise .8s cubic-bezier(.2,.7,.2,1) forwards}
.rs-tag span:nth-child(1){animation-delay:2.9s}.rs-tag span:nth-child(2){animation-delay:3.15s}.rs-tag span:nth-child(3){animation-delay:3.4s;color:#E3C98C;font-weight:700}
.rs-ver{margin:14px 0 0;font-size:.72rem;font-weight:800;letter-spacing:.3em;text-transform:uppercase;color:#C6A15B;opacity:0;animation:rsRise .9s 3.7s cubic-bezier(.2,.7,.2,1) forwards;z-index:1}
.rs-by img{border-radius:50%;display:block}
.rs-by{position:absolute;bottom:calc(28px + env(safe-area-inset-bottom,0px));display:flex;align-items:center;gap:10px;font-size:9px;letter-spacing:.3em;color:rgba(247,242,232,.5);opacity:0;animation:rsRise .9s 3.9s ease forwards;z-index:1}
@keyframes rsRise{to{opacity:1;transform:none}}
.rs-prog{position:absolute;left:12%;right:12%;bottom:calc(8px + env(safe-area-inset-bottom,0px));height:2px;border-radius:2px;background:rgba(247,242,232,.08);z-index:1}
.rs-prog i{display:block;position:relative;height:100%;border-radius:2px;transform-origin:left;transform:scaleX(0);background:linear-gradient(90deg,rgba(198,161,91,0),#C6A15B 40%,#F3E1AC);box-shadow:0 0 10px rgba(198,161,91,.8);animation:rsFill ${INTRO_MS}ms cubic-bezier(.35,0,.25,1) forwards}
@keyframes rsFill{to{transform:scaleX(1)}}
.rs-skip{position:absolute;z-index:2;top:calc(16px + env(safe-area-inset-top,0px));right:calc(16px + env(safe-area-inset-right,0px));border:1px solid rgba(198,161,91,.45);background:rgba(16,37,66,.55);color:#F7F2E8;border-radius:999px;padding:9px 16px;min-height:40px;font:600 .86rem 'Outfit',sans-serif;letter-spacing:.04em;cursor:pointer;-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px)}
.rs-skip:hover{background:rgba(36,59,83,.8)}
/* split-screen wipe: the finished splash is cut on the logo's centre line; the top half wipes up, the bottom half wipes down */
.rs-veil{position:fixed;inset:0;z-index:5100;pointer-events:none}
.rs-veil.live{pointer-events:auto}
.rs-h2{position:absolute;left:0;right:0;overflow:hidden;will-change:transform;transition:transform 1.5s cubic-bezier(.65,0,.25,1)}
.rs-h2.t{top:0}
.rs-veil.cut .rs-h2.t{transform:translateY(-7px);transition-duration:.35s}
.rs-veil.cut .rs-h2.b{transform:translateY(7px);transition-duration:.35s}
.rs-veil.cut .rs-h2.t::after,.rs-veil.cut .rs-h2.b::before{height:3px;background:linear-gradient(90deg,transparent,#FFF4D0 20%,#FFF4D0 80%,transparent);box-shadow:0 0 22px 4px rgba(243,225,172,.95)}
.rs-veil.cut .rs-h2.t::after{bottom:-1px}.rs-veil.cut .rs-h2.b::before{top:-1px}
.rs-veil.opening .rs-h2.t{transform:translateY(-101%)}
.rs-veil.opening .rs-h2.b{transform:translateY(101%)}
.rs-h2.t::after,.rs-h2.b::before{content:"";position:absolute;left:0;right:0;height:1px;z-index:3;background:linear-gradient(90deg,transparent,rgba(227,201,140,.95),transparent);box-shadow:0 0 14px rgba(198,161,91,.7)}
.rs-h2.t::after{bottom:0}.rs-h2.b::before{top:0}
.rs-clone{position:absolute!important;inset:auto!important;left:0!important;z-index:auto!important;opacity:1!important;pointer-events:none}
.rs-clone *{animation:none!important}
.rs-clone::before{opacity:1!important}
.rs-clone .rs-logo-wrap,.rs-clone .rs-word span,.rs-clone .rs-tag span,.rs-clone .rs-ver,.rs-clone .rs-by{opacity:1!important;transform:none!important;filter:none!important}
.rs-clone .rs-logo-wrap{transform:translate(-50%,-50%)!important}
.rs-clone .rs-rule{width:min(62vw,240px)!important}
@media (prefers-reduced-motion:reduce){.rs-veil{display:none!important}.rs-logo-wrap,.rs-word span,.rs-tag span,.rs-ver,.rs-by,.rs-root::before{animation:none;opacity:1;transform:none;filter:none}.rs-rule{animation:none;width:min(62vw,240px)}.rs-flare,.rs-shine{display:none}.rs-prog i{animation:none;transform:scaleX(1)}}
`;

// Gold hairline rings that draw outward from the mark, a soft radar sweep, and people-dots that land on the rings.
function drawRadar(canvas, reduced) {
  const ctx = canvas.getContext("2d");
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const size = canvas.getBoundingClientRect().width || 320;
  canvas.width = canvas.height = Math.round(size * dpr);
  ctx.scale(dpr, dpr);
  const c = size / 2, R = size * 0.485;
  const rnd = (a, b) => a + Math.random() * (b - a);
  const ease = x => 1 - Math.pow(1 - x, 3);
  const rings = [[0.5, 0.2], [0.68, 0.45], [0.84, 0.7], [1, 0.95]];       // [radius k, start s]
  const motes = Array.from({ length: 46 }, () => ({ a: rnd(0, Math.PI * 2), r: rnd(0.35, 1.25) * R, s: rnd(0.6, 1.6), sp: rnd(0.04, 0.14) * (Math.random() < 0.5 ? -1 : 1), tw: rnd(0, 6) }));
  const dots = [[-0.7, -0.52, "247,242,232", 2.6], [0.62, -0.38, "227,201,140", 3.0], [0.74, 0.34, "247,242,232", 3.4], [-0.36, 0.78, "227,201,140", 3.8], [-0.82, 0.18, "198,161,91", 4.2]];
  const t0 = performance.now();
  let raf;
  const frame = now => {
    const t = reduced ? 9 : (now - t0) / 1000;
    ctx.clearRect(0, 0, size, size);
    // rings drawing outward
    rings.forEach(([k, st], i) => {
      const p = ease(Math.min(1, Math.max(0, (t - st) / 1.3)));
      if (p <= 0) return;
      ctx.beginPath(); ctx.arc(c, c, R * k * (0.55 + 0.45 * p), 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(198,161,91," + ((i === 3 ? 0.55 : 0.28) * p).toFixed(3) + ")"; ctx.lineWidth = i === 3 ? 1.4 : 1; if (i === 1) ctx.setLineDash([2, 7]); else ctx.setLineDash([]);
      ctx.stroke();
    });
    ctx.setLineDash([]);
    // outer tick ring
    const tp = ease(Math.min(1, Math.max(0, (t - 0.9) / 1.4)));
    for (let i = 0; i < 90; i++) {
      if (i / 90 > tp) break;
      const a = (i / 90) * Math.PI * 2 - Math.PI / 2, long = i % 5 === 0;
      ctx.beginPath(); ctx.moveTo(c + Math.cos(a) * R * 1.0, c + Math.sin(a) * R * 1.0); ctx.lineTo(c + Math.cos(a) * R * (long ? 0.94 : 0.97), c + Math.sin(a) * R * (long ? 0.94 : 0.97));
      ctx.strokeStyle = "rgba(227,201,140," + (long ? 0.7 : 0.35) + ")"; ctx.lineWidth = long ? 1.4 : 1; ctx.stroke();
    }
    // sweep
    const sweepOn = t > 1.6 && !reduced, sw = ((t - 1.6) * 1.5) % (Math.PI * 2);
    if (sweepOn) {
      const fade = Math.min(1, (t - 1.6) / 0.8);
      for (let i = 0; i < 36; i++) {
        const a0 = sw - i * 0.03, a1 = a0 + 0.032;
        ctx.beginPath(); ctx.moveTo(c, c); ctx.arc(c, c, R * 0.995, a0, a1); ctx.closePath();
        ctx.fillStyle = "rgba(227,201,140," + (0.2 * fade * (1 - i / 36)).toFixed(3) + ")"; ctx.fill();
      }
      ctx.beginPath(); ctx.moveTo(c, c); ctx.lineTo(c + Math.cos(sw) * R * 0.995, c + Math.sin(sw) * R * 0.995);
      ctx.strokeStyle = "rgba(243,225,172," + (0.55 * fade).toFixed(2) + ")"; ctx.lineWidth = 1.4; ctx.stroke();
    }
    // drifting gold motes
    motes.forEach(m => {
      const a = m.a + t * m.sp, x = c + Math.cos(a) * m.r, y = c + Math.sin(a) * m.r;
      const al = Math.min(1, t / 1.5) * (0.25 + 0.35 * Math.sin(t * 1.3 + m.tw) ** 2);
      ctx.beginPath(); ctx.arc(x, y, m.s, 0, Math.PI * 2); ctx.fillStyle = "rgba(227,201,140," + al.toFixed(2) + ")"; ctx.fill();
    });
    // people dots landing
    dots.forEach(([bx, by, col, at]) => {
      const bt = reduced ? 3 : t - at; if (bt < 0) return;
      const x = c + bx * R * 0.9, y = c + by * R * 0.9, ring = Math.min(1, bt / 1.3), pop = Math.min(1, bt / 0.35);
      ctx.beginPath(); ctx.arc(x, y, 4 * ease(pop), 0, Math.PI * 2); ctx.fillStyle = "rgba(" + col + ",1)"; ctx.shadowColor = "rgb(" + col + ")"; ctx.shadowBlur = 12; ctx.fill(); ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(x, y, 4 + ring * 22, 0, Math.PI * 2); ctx.strokeStyle = "rgba(" + col + "," + (0.65 * (1 - ring)).toFixed(2) + ")"; ctx.lineWidth = 1.5; ctx.stroke();
    });
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return () => cancelAnimationFrame(raf);
}

// Sign-in screen arrival (same idea as Optimum Brain): the brand rises, then the film strip, promo and
// feature cards lift out of a blur one after another, and the sign-in form arrives last.
export const LOGIN_ARRIVE_CSS = `
.lg-hold .lg-a{opacity:0}
.lg-go .lg-a{opacity:0;animation:lgIn 1.3s cubic-bezier(.22,.8,.2,1) forwards}
.lg-go .lg-a1{animation-name:lgBrand;animation-duration:1.3s;animation-delay:1.35s}
.lg-go .lg-a2{animation-delay:.5s}.lg-go .lg-a3{animation-delay:.75s}.lg-go .lg-a4{animation-delay:1s}
.lg-go .lg-c0{animation-delay:1.1s}.lg-go .lg-c1{animation-delay:1.3s}.lg-go .lg-c2{animation-delay:1.5s}
.lg-go .lg-a6{animation-delay:1.7s;animation-duration:1.3s}
@keyframes lgBrand{from{opacity:0;transform:translateY(-16px) scale(.96);filter:blur(6px)}to{opacity:1;transform:none;filter:none}}
@keyframes lgIn{from{opacity:0;transform:translateY(22px);filter:blur(10px)}to{opacity:1;transform:none;filter:none}}
@media (prefers-reduced-motion:reduce){.lg-hold .lg-a,.lg-go .lg-a{opacity:1;animation:none}}
`;

export default function SplashIntro({ version, onDone, onOpen }) {
  const canvasRef = useRef(null);
  const veilRef = useRef(null);
  const [gone, setGone] = useState(false);
  const started = useRef(false);
  const runRef = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    const reduced = REDUCED();
    const stop = canvasRef.current ? drawRadar(canvasRef.current, reduced) : () => {};
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(fn, ms));
    const run = () => {
      if (started.current) return; started.current = true;
      if (reduced || !veilRef.current) { setGone(true); if (onOpenRef.current) onOpenRef.current(); doneRef.current(); return; }
      const v = veilRef.current, root = document.querySelector(".rs-root");
      const lg = root && root.querySelector(".rs-logo-wrap img");
      if (!root || !lg) { setGone(true); if (onOpenRef.current) onOpenRef.current(); doneRef.current(); return; }
      // freeze the finished splash into two halves cut on the logo's own centre line
      const r = lg.getBoundingClientRect(), seam = Math.round(r.top + r.height / 2);
      const W = window.innerWidth, H = window.innerHeight;
      const srcCv = root.querySelector("canvas");
      const mk = top => {
        const h = document.createElement("div");
        h.className = "rs-h2 " + (top ? "t" : "b");
        h.style.top = top ? "0" : seam + "px"; h.style.height = top ? seam + "px" : (H - seam) + "px";
        const c = root.cloneNode(true);
        c.classList.add("rs-clone");
        ["rs-skip", "rs-flare", "rs-shine", "rs-prog"].forEach(k => c.querySelectorAll("." + k).forEach(n => n.remove()));
        const dst = c.querySelector("canvas");
        if (dst && srcCv) { dst.width = srcCv.width; dst.height = srcCv.height; try { dst.getContext("2d").drawImage(srcCv, 0, 0); } catch (e) { /* ignore */ } }
        c.style.setProperty("width", W + "px", "important"); c.style.setProperty("height", H + "px", "important"); c.style.setProperty("top", (top ? 0 : -seam) + "px", "important");
        h.appendChild(c); return h;
      };
      v.innerHTML = ""; v.className = "rs-veil live";
      v.appendChild(mk(true)); v.appendChild(mk(false));
      void v.offsetWidth;
      setGone(true);                       // the real splash disappears; the two frozen halves look identical
      later(() => v.classList.add("cut"), 120);          // the logo visibly splits: halves part a hair, a bright cut line flashes
      later(() => {
        v.classList.remove("cut"); v.classList.add("opening");        // top half wipes up, bottom half wipes down
        if (onOpenRef.current) onOpenRef.current();   // the sign-in screen starts showing through the seam
        later(() => { v.className = "rs-veil"; v.innerHTML = ""; doneRef.current(); }, 1700);
      }, 700);
    };
    runRef.current = run;
    later(run, reduced ? 1200 : INTRO_MS);
    const key = e => { if (e.key === "Escape" || e.key === "Enter") run(); };
    document.addEventListener("keydown", key);
    return () => { stop(); timers.forEach(clearTimeout); document.removeEventListener("keydown", key); };
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className={"rs-root" + (gone ? " rs-gone" : "")} role="status" aria-label={"Loading Oscar Radar version " + version}>
        <button type="button" className="rs-skip" onClick={() => runRef.current && runRef.current()}>Skip &rsaquo;</button>
        <div className="rs-stage">
          <canvas ref={canvasRef} aria-hidden="true" />
          <span className="rs-flare" aria-hidden="true" /><span className="rs-flare f2" aria-hidden="true" />
          <div className="rs-logo-wrap"><img src={LOGO} alt="" /><span className="rs-shine" aria-hidden="true" /></div>
        </div>
        <h1 className="rs-word" aria-label="Oscar Radar">{WORD.map((ch, k) => <span key={k} className={ch === " " ? "sp" : ""} style={{ animationDelay: (1.9 + k * 0.07).toFixed(2) + "s" }} aria-hidden="true">{ch === " " ? "\u00A0" : ch}</span>)}</h1>
        <span className="rs-rule" aria-hidden="true" />
        <p className="rs-tag"><span>Who is</span> <span>on your</span> <span>radar?</span></p>
        <p className="rs-ver">Version {version}</p>
        <div className="rs-by"><img src={(process.env.PUBLIC_URL || "") + "/candia-ai-logo.png"} alt="" width="26" height="26" /><span>A CANDIA.AI COMPANY</span></div>
        <div className="rs-prog" aria-hidden="true"><i /></div>
      </div>
      <div className="rs-veil" ref={veilRef} aria-hidden="true">
        
      </div>
    </>
  );
}
